import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  fetchConfigurationsService,
  fetchOrgDetailsService,
  fetchVoicesService,
  saveConfigService,
  saveWhitelabelConfigService,
  uploadWhitelabelLogoService,
  fetchEmailMonitorStatusService,
  toggleEmailMonitorService,
  disconnectEmailMonitorService,
  connectEmailMonitorPasswordService,
} from '../../Services/configuration.service';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import {
  Save,
  Play,
  Pause,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Check,
  Loader2,
  Upload,
  X,
  AlertCircle,
  Mail,
  CheckCircle2,
  Link,
  Unlink,
  Eye,
  EyeOff,
  KeyRound,
  Settings,
  Shield,
  Palette,
  Info,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { clearBrandingCache } from '../../utils/whitelabel';
import { useTheme } from '../../hooks/useTheme';
import EditCompanyProfile from './EditCompanyProfile';

const CONFIG_BOXES = [
  {
    id: 'company_profile',
    title: 'Company Profile',
    icon: Settings,
    description: 'Company information and contact details',
  },
  {
    id: 'interview_config',
    title: 'Interview Settings',
    icon: Settings,
    description: 'Resume score, interview score, language, voice & proctoring',
  },
  {
    id: 'whitelabel',
    title: 'White Labelling',
    icon: Palette,
    description: 'Company branding, logos & customization',
  },
  {
    id: 'email_agent',
    title: 'Email Agent',
    icon: Mail,
    description: 'Email job application monitor',
  },
];

const AVATAR_VOICES = [
  {
    id: 'b2bd6569-a537-4342-aeca-a1f15d2a2c97',
    name: 'Rika',
    gender: 'female',
    style: 'IA',
  },
  {
    id: '8a504f9b-95dd-42d4-8b0c-edc2567b6382',
    name: 'Marianne',
    gender: 'female',
    style: 'IA',
  },
  {
    id: '864a26b8-bfba-4435-9cc5-1dd593de5ca7',
    name: 'Katya',
    gender: 'female',
    style: 'IA',
  },
  {
    id: '62bbb4b2-bb26-4727-bc87-cfb2bd4e0cc8',
    name: 'June',
    gender: 'female',
    style: 'Lifelike',
  },
  {
    id: '4f3b1e99-b580-4f05-9b67-a5f585be0232',
    name: 'Judy',
    gender: 'female',
    style: 'Professional',
  },
  {
    id: '254ffe1e-c89f-430f-8c36-9e7611d310c0',
    name: 'Elenora',
    gender: 'female',
    style: 'Professional',
  },
  {
    id: 'de5574fc-009e-4a01-a881-9919ef8f5a0c',
    name: 'Ann',
    gender: 'female',
    style: 'IA',
  },
  {
    id: '3607df3c-9de0-4274-b0be-7e035775ead5',
    name: 'Anastasia',
    gender: 'female',
    style: 'IA',
  },
  {
    id: 'e948b062-7dce-4f2b-bcf6-98bd3511106b',
    name: 'Amina',
    gender: 'female',
    style: 'IA',
  },
  {
    id: 'c84af063-5ce2-4370-8ef8-dcd0ef903d43',
    name: 'Alessandra',
    gender: 'female',
    style: 'IA',
  },
  {
    id: 'c2527536-6d1f-4412-a643-53a3497dada9',
    name: 'Wayne Liang',
    gender: 'male',
    style: '',
  },
  {
    id: '83a26e3f-bcff-4887-80a2-17531c342c9e',
    name: 'Thaddeus',
    gender: 'male',
    style: 'IA',
  },
  {
    id: 'b139a8fe-7240-4454-ac37-8c68aebcee41',
    name: 'Silas',
    gender: 'male',
    style: 'Lifelike',
  },
  {
    id: '51afbab6-7af4-473b-95fc-6ce26aac8bb1',
    name: 'Shawn',
    gender: 'male',
    style: 'IA',
  },
  {
    id: 'bcc6960c-b2d3-41e9-a8a5-2780ce6abe2e',
    name: 'Santa',
    gender: 'male',
    style: 'Public',
  },
  {
    id: '98a984cd-5f25-49b1-8844-2195c3d50e0f',
    name: 'Pedro',
    gender: 'male',
    style: 'IA',
  },
  {
    id: 'e04e9d57-853f-4d72-a8ff-8e3c768f4c9c',
    name: 'Graham',
    gender: 'male',
    style: 'IA',
  },
  {
    id: 'b952f553-f7f3-4e52-8625-86b4c415384f',
    name: 'Dexter',
    gender: 'male',
    style: 'Professional',
  },
  {
    id: '9c8b542a-bf5c-4f4c-9011-75c79a274387',
    name: 'Bryan',
    gender: 'male',
    style: 'Professional',
  },
  {
    id: 'c466083f-30f0-465b-a836-0b77abfe7956',
    name: 'Anthony',
    gender: 'male',
    style: 'IA',
  },
];

// Skeleton Loader Component
const SkeletonLoader = ({ className = '' }) => (
  <div
    className={`animate-pulse dark:bg-gray-700/50 bg-gray-200 rounded ${className}`}
  ></div>
);

const AssessmentConfig = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [selectedBox, setSelectedBox] = useState('company_profile');
  const [initialConfig, setInitialConfig] = useState(null);
  const [companyProfileData, setCompanyProfileData] = useState(null);

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isVoicesLoading, setIsVoicesLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Configuration states
  const [resumeScoreThreshold, setResumeScoreThreshold] = useState(50);
  const [interviewScoreThreshold, setInterviewScoreThreshold] = useState(50);
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [isVideoIncluded, setIsVideoIncluded] = useState(true);
  const [isSecondaryCameraEnabled, setIsSecondaryCameraEnabled] =
    useState(false);

  // White-label configuration states
  const [whitelabelEnabled, setWhitelabelEnabled] = useState(false);
  const [companyDisplayName, setCompanyDisplayName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [atsSubdomain, setAtsSubdomain] = useState('');

  // Primary logo states
  const [logoPrimaryPreview, setLogoPrimaryPreview] = useState('');
  const [logoPrimaryDimensions, setLogoPrimaryDimensions] = useState(null);
  const [isUploadingPrimary, setIsUploadingPrimary] = useState(false);
  // Primary logo DARK theme states (NEW)
  const [logoPrimaryDarkPreview, setLogoPrimaryDarkPreview] = useState('');
  const [logoPrimaryDarkDimensions, setLogoPrimaryDarkDimensions] =
    useState(null);
  const [isUploadingPrimaryDark, setIsUploadingPrimaryDark] = useState(false);

  // Compact logo states
  const [logoCompactPreview, setLogoCompactPreview] = useState('');
  const [logoCompactDimensions, setLogoCompactDimensions] = useState(null);
  const [isUploadingCompact, setIsUploadingCompact] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audio, setAudio] = useState(null);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Avatar interview voice selection (shown only when live_avatar_enabled)
  const [liveAvatarEnabled, setLiveAvatarEnabled] = useState(false);
  const [selectedAvatarVoice, setSelectedAvatarVoice] = useState(null);
  const [avatarPlayingId, setAvatarPlayingId] = useState(null);

  // Derived state to check if any changes were made compared to initial load
  const hasChanges = useMemo(() => {
    if (!initialConfig) return false;

    const currentVoice = selectedVoice || {};
    const initialVoice = initialConfig.voice || {};

    const currentAvatarVoiceId = selectedAvatarVoice?.id || null;
    const initialAvatarVoiceId = initialConfig.liveAvatarVoiceId || null;

    return (
      resumeScoreThreshold !== initialConfig.resumeScoreThreshold ||
      interviewScoreThreshold !== initialConfig.interviewScoreThreshold ||
      selectedLanguage !== initialConfig.selectedLanguage ||
      currentVoice.name !== initialVoice.name ||
      currentVoice.type !== initialVoice.type ||
      currentVoice.provider !== initialVoice.provider ||
      isSecondaryCameraEnabled !== initialConfig.isSecondaryCameraEnabled ||
      whitelabelEnabled !== initialConfig.whitelabelEnabled ||
      companyDisplayName !== initialConfig.companyDisplayName ||
      contactEmail !== initialConfig.contactEmail ||
      atsSubdomain !== initialConfig.atsSubdomain ||
      logoPrimaryPreview !== (initialConfig.logoPrimaryPreview || '') ||
      logoPrimaryDarkPreview !== (initialConfig.logoPrimaryDarkPreview || '') ||
      logoCompactPreview !== (initialConfig.logoCompactPreview || '') ||
      liveAvatarEnabled !== initialConfig.liveAvatarEnabled ||
      currentAvatarVoiceId !== initialAvatarVoiceId
    );
  }, [
    initialConfig,
    resumeScoreThreshold,
    interviewScoreThreshold,
    selectedLanguage,
    selectedVoice,
    isSecondaryCameraEnabled,
    whitelabelEnabled,
    companyDisplayName,
    contactEmail,
    atsSubdomain,
    logoPrimaryPreview,
    logoPrimaryDarkPreview,
    logoCompactPreview,
    liveAvatarEnabled,
    selectedAvatarVoice,
  ]);

  // Email monitor states
  const [emailMonitorSession, setEmailMonitorSession] = useState(null);
  const [emailMonitorLoading, setEmailMonitorLoading] = useState(true);
  const [emailMonitorToggling, setEmailMonitorToggling] = useState(false);
  const [emailMonitorDisconnecting, setEmailMonitorDisconnecting] =
    useState(false);
  const [pwForm, setPwForm] = useState({
    show: false,
    email: '',
    password: '',
    provider: 'gmail',
    showPass: false,
    connecting: false,
  });
  const [showAppPasswordGuide, setShowAppPasswordGuide] = useState(false);

  // Constants
  const BUCKET_NAME = process.env.REACT_APP_BUCKET_NAME;
  const AWS_REGION_NAME = process.env.REACT_APP_AWS_REGION_NAME;

  // Email monitor handlers
  const fetchEmailMonitorStatus = useCallback(async () => {
    setEmailMonitorLoading(true);
    try {
      const data = await fetchEmailMonitorStatusService();
      const connected = data.connected ? data : null;
      setEmailMonitorSession(connected);
      if (!connected)
        setPwForm({
          show: false,
          email: '',
          password: '',
          provider: 'gmail',
          showPass: false,
          connecting: false,
        });
    } catch {
      setEmailMonitorSession(null);
      setPwForm({
        show: false,
        email: '',
        password: '',
        provider: 'gmail',
        showPass: false,
        connecting: false,
      });
    } finally {
      setEmailMonitorLoading(false);
    }
  }, []);

  const handleToggleEmailMonitor = async () => {
    if (!emailMonitorSession) return;
    setEmailMonitorToggling(true);
    const action = emailMonitorSession.status === 'active' ? 'pause' : 'resume';
    const newStatus = action === 'pause' ? 'paused' : 'active';
    try {
      await toggleEmailMonitorService(action);
      setEmailMonitorSession((prev) => ({ ...prev, status: newStatus }));
      toast.success(
        `Email monitor ${newStatus === 'active' ? 'resumed' : 'paused'}`,
      );
    } catch {
      toast.error('Failed to update email monitor status');
    } finally {
      setEmailMonitorToggling(false);
    }
  };

  const handleDisconnectEmailMonitor = async () => {
    if (
      !window.confirm(
        'Disconnect email monitor? This will stop monitoring your inbox.',
      )
    )
      return;
    setEmailMonitorDisconnecting(true);
    try {
      await disconnectEmailMonitorService();
      setEmailMonitorSession(null);
      toast.success('Email monitor disconnected');
    } catch {
      toast.error('Failed to disconnect email monitor');
    } finally {
      setEmailMonitorDisconnecting(false);
    }
  };

  const handleConnectPassword = async (e) => {
    e.preventDefault();
    setPwForm((f) => ({ ...f, connecting: true }));
    try {
      await connectEmailMonitorPasswordService({
        email: pwForm.email,
        app_password: pwForm.password,
        provider: pwForm.provider,
      });
      toast.success('Inbox connected successfully!');
      setPwForm({
        show: false,
        email: '',
        password: '',
        provider: 'gmail',
        showPass: false,
        connecting: false,
      });
      await fetchEmailMonitorStatus();
    } catch {
      toast.error('Failed to connect. Check your email and app password.');
      setPwForm((f) => ({ ...f, connecting: false }));
    }
  };

  useEffect(() => {
    fetchEmailMonitorStatus();
  }, [fetchEmailMonitorStatus]);

  const handleLanguageChange = useCallback(
    async (language, preserveSelectedVoice = false, isInitialLoad = false) => {
      if (!language) return;

      setSelectedLanguage(language);
      if (!preserveSelectedVoice) {
        setSelectedVoice(null);
      }

      // Only show voice loading skeleton if not during initial page load
      if (!isInitialLoad) {
        setIsVoicesLoading(true);
      }

      try {
        const data = await fetchVoicesService(language);
        setVoices(data);
      } catch (error) {
        console.error('Error fetching voices:', error);
        setVoices([]);
        if (!isInitialLoad) {
          toast.error('Failed to load voices for selected language');
        }
      } finally {
        if (!isInitialLoad) {
          setIsVoicesLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        setIsLoading(true);
        const [data, orgProfileRes] = await Promise.all([
          fetchConfigurationsService(),
          fetchOrgDetailsService().catch(() => null),
        ]);

        if (orgProfileRes) setCompanyProfileData(orgProfileRes);

        setLanguages([...(data.available_languages || [])].sort());
        setSelectedLanguage(data.language || '');
        setResumeScoreThreshold(data.resume_score_threshold || 50);
        setInterviewScoreThreshold(data.interview_score_threshold || 50);
        setIsVideoIncluded(true);
        setIsSecondaryCameraEnabled(data.secondary_camera_enabled || false);

        if (data.voice_name && data.voice_type) {
          const currentVoiceConfig = {
            name: data.voice_name,
            type: data.voice_type,
            provider: data.voice_provider || 'aws_polly',
          };
          setSelectedVoice(currentVoiceConfig);
        }

        if (data.language) {
          await handleLanguageChange(data.language, true, true);
        }

        setWhitelabelEnabled(data.whitelabel_enabled || false);
        const orgName = data.organization_name || '';
        setCompanyDisplayName(
          data.whitelabel_config?.company_display_name || orgName,
        );

        setContactEmail(data.whitelabel_config?.contact_email || '');

        const primaryLogo =
          data.whitelabel_config?.logo_primary ||
          data.whitelabel_config?.logo_black ||
          data.whitelabel_config?.logo_email;
        if (primaryLogo) setLogoPrimaryPreview(primaryLogo);

        const primaryDarkLogo = data.whitelabel_config?.logo_primary_dark;
        if (primaryDarkLogo) setLogoPrimaryDarkPreview(primaryDarkLogo);

        const compactLogo = data.whitelabel_config?.logo_compact;
        if (compactLogo) setLogoCompactPreview(compactLogo);

        setAtsSubdomain(data.whitelabel_config?.ats_subdomain || '');

        // LiveAvatar voice
        setLiveAvatarEnabled(data.live_avatar_enabled || false);
        if (data.live_avatar_voice_id) {
          const match = AVATAR_VOICES.find(
            (voice) => voice.id === data.live_avatar_voice_id,
          );
          if (match) setSelectedAvatarVoice(match);
        }

        // Capture the initial baseline for change detection
        setInitialConfig({
          resumeScoreThreshold: data.resume_score_threshold || 50,
          interviewScoreThreshold: data.interview_score_threshold || 50,
          selectedLanguage: data.language || '',
          voice: {
            name: data.voice_name || null,
            type: data.voice_type || null,
            provider: data.voice_provider || 'aws_polly',
          },
          isSecondaryCameraEnabled: data.secondary_camera_enabled || false,
          whitelabelEnabled: data.whitelabel_enabled || false,
          companyDisplayName:
            data.whitelabel_config?.company_display_name || orgName,
          contactEmail: data.whitelabel_config?.contact_email || '',
          atsSubdomain: data.whitelabel_config?.ats_subdomain || '',
          logoPrimaryPreview: primaryLogo || '',
          logoPrimaryDarkPreview: primaryDarkLogo || '',
          logoCompactPreview: compactLogo || '',
          liveAvatarEnabled: data.live_avatar_enabled || false,
          liveAvatarVoiceId: data.live_avatar_voice_id || null,
        });

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching configurations:', error);
        toast.error('Failed to load configuration data');
        setIsLoading(false);
      }
    };

    fetchConfigurations();
  }, [handleLanguageChange]);

  // Ensure selected voice is maintained when voices are loaded
  useEffect(() => {
    if (voices.length > 0 && selectedVoice && !isVoicesLoading) {
      // Check if current selected voice exists in the loaded voices
      const voiceExists = voices.find(
        (voice) =>
          voice.name === selectedVoice.name &&
          voice.provider === selectedVoice.provider,
      );

      if (!voiceExists) {
        console.warn(
          'Selected voice not found in loaded voices, keeping selection for persistence',
        );
        // Don't reset selectedVoice - keep it for proper display even if not in current list
      }
    }
  }, [voices, selectedVoice, isVoicesLoading]);

  useEffect(() => {
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
        setIsPlaying(false);
      }
    };
  }, [audio]);

  const handleLogoUpload = useCallback(
    async (event, logoType) => {
      const file = event.target.files[0];
      if (!file) return;

      event.target.value = '';

      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file (PNG, JPG, JPEG)');
        return;
      }

      if (file.type === 'image/svg+xml') {
        toast.error('SVG files are not supported. Please use PNG or JPG');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        return;
      }

      try {
        // Set loading state based on logo type
        if (logoType === 'primary') {
          setIsUploadingPrimary(true);
        } else if (logoType === 'primary_dark') {
          // NEW
          setIsUploadingPrimaryDark(true);
        } else {
          setIsUploadingCompact(true);
        }

        const img = new Image();
        const imageUrl = URL.createObjectURL(file);

        img.onload = async () => {
          URL.revokeObjectURL(imageUrl);

          const width = img.width;
          const height = img.height;

          // Different validation rules per logo type
          if (logoType === 'primary' || logoType === 'primary_dark') {
            if (width < 100 || height < 30) {
              toast.error('Primary logo too small. Minimum: 100×30px');
              if (logoType === 'primary') setIsUploadingPrimary(false);
              else setIsUploadingPrimaryDark(false); // NEW
              return;
            }
            if (width > 2000 || height > 800) {
              toast.error('Primary logo too large. Maximum: 2000×800px');
              if (logoType === 'primary') setIsUploadingPrimary(false);
              else setIsUploadingPrimaryDark(false); // NEW
              return;
            }
          } else {
            // Compact logo validation
            if (width < 64 || height < 64) {
              toast.error('Compact logo too small. Minimum: 64×64px');
              setIsUploadingCompact(false);
              return;
            }
            if (width > 512 || height > 512) {
              toast.error('Compact logo too large. Maximum: 512×512px');
              setIsUploadingCompact(false);
              return;
            }

            // Warn if not square
            const aspectRatio = width / height;
            if (aspectRatio < 0.8 || aspectRatio > 1.2) {
              toast.warning('Compact logo should be square for best results');
            }
          }

          try {
            const responseData = await uploadWhitelabelLogoService(file, logoType);

            if (responseData && responseData.logo_url) {
              if (logoType === 'primary') {
                setLogoPrimaryPreview(responseData.logo_url);
                setLogoPrimaryDimensions({ width, height });
                toast.success(
                  `Primary logo (light) uploaded! (${width}×${height}px)`,
                );
              } else if (logoType === 'primary_dark') {
                setLogoPrimaryDarkPreview(responseData.logo_url);
                setLogoPrimaryDarkDimensions({ width, height });
                toast.success(
                  `Primary logo (dark) uploaded! (${width}×${height}px)`,
                );
              } else {
                setLogoCompactPreview(responseData.logo_url);
                setLogoCompactDimensions({ width, height });
                toast.success(`Compact logo uploaded! (${width}×${height}px)`);
              }
            }
          } catch (error) {
            console.error('Error uploading logo:', error);
            const errorMsg =
              error.response?.data?.detail ||
              'Failed to upload logo. Please try again.';
            toast.error(errorMsg);
          } finally {
            if (logoType === 'primary') {
              setIsUploadingPrimary(false);
            } else if (logoType === 'primary_dark') {
              // NEW
              setIsUploadingPrimaryDark(false);
            } else {
              setIsUploadingCompact(false);
            }
          }
        };

        img.onerror = () => {
          URL.revokeObjectURL(imageUrl);
          toast.error('Failed to load image. Please try a different file.');
          if (logoType === 'primary') {
            setIsUploadingPrimary(false);
          } else if (logoType === 'primary_dark') {
            // NEW
            setIsUploadingPrimaryDark(false);
          } else {
            setIsUploadingCompact(false);
          }
        };

        img.src = imageUrl;
      } catch (error) {
        console.error('Error processing logo:', error);
        toast.error('Failed to process logo. Please try again.');
        if (logoType === 'primary') {
          setIsUploadingPrimary(false);
        } else if (logoType === 'primary_dark') {
          // NEW
          setIsUploadingPrimaryDark(false);
        } else {
          setIsUploadingCompact(false);
        }
      }
    },
    [],
  );

  const handleRemoveLogo = useCallback((logoType) => {
    if (logoType === 'primary') {
      setLogoPrimaryPreview('');
      setLogoPrimaryDimensions(null);
      toast.info('Primary logo (light) removed. Remember to save changes.');
    } else if (logoType === 'primary_dark') {
      setLogoPrimaryDarkPreview('');
      setLogoPrimaryDarkDimensions(null);
      toast.info('Primary logo (dark) removed. Remember to save changes.');
    } else {
      setLogoCompactPreview('');
      setLogoCompactDimensions(null);
      toast.info('Compact logo removed. Remember to save changes.');
    }
  }, []);

  const handleSaveGeneralConfig = useCallback(
    async (whitelabelOverride = null) => {
      if (isSaving) return;

      const effectiveWhitelabelEnabled =
        typeof whitelabelOverride === 'boolean'
          ? whitelabelOverride
          : whitelabelEnabled;

      if (
        resumeScoreThreshold <= 0 ||
        resumeScoreThreshold >= 100 ||
        !resumeScoreThreshold
      ) {
        setSelectedBox('interview_config');
        toast.warn(
          "Please provide a valid Resume Score Threshold in the 'Interview Settings' tab.",
        );
        return;
      }
      if (
        interviewScoreThreshold <= 0 ||
        interviewScoreThreshold >= 100 ||
        !interviewScoreThreshold
      ) {
        setSelectedBox('interview_config');
        toast.warn(
          "Please provide a valid Interview Score Threshold in the 'Interview Settings' tab.",
        );
        return;
      }

      if (!selectedLanguage) {
        setSelectedBox('interview_config');
        toast.warn(
          "Please select an interview language in the 'Interview Settings' tab.",
        );
        return;
      }

      if (
        !liveAvatarEnabled &&
        (!selectedVoice || !selectedVoice.name || !selectedVoice.type)
      ) {
        setSelectedBox('interview_config');
        toast.warn(
          "Please select an interviewer voice in the 'Interview Settings' tab.",
        );
        return;
      }

      if (liveAvatarEnabled && !selectedAvatarVoice?.id) {
        setSelectedBox('interview_config');
        toast.warn(
          "Please select an Avatar Interview voice in the 'Interview Settings' tab.",
        );
        return;
      }

      // Validate white-label logo requirements
      if (effectiveWhitelabelEnabled) {
        if (!logoPrimaryPreview) {
          setSelectedBox('whitelabel');
          toast.warn(
            "A Primary logo is required in the 'White Labelling' tab when white-labeling is enabled.",
          );
          return;
        }
        if (!companyDisplayName || !companyDisplayName.trim()) {
          setSelectedBox('whitelabel');
          toast.warn(
            "Company display name is required in the 'White Labelling' tab when white-labeling is enabled.",
          );
          return;
        }
      }

      try {
        setIsSaving(true);

        const data = await saveConfigService({
          resume_score_threshold: resumeScoreThreshold,
          interview_score_threshold: interviewScoreThreshold,
          language: selectedLanguage,
          ...(selectedVoice
            ? {
                voice_name: selectedVoice.name,
                voice_type: selectedVoice.type,
                voice_provider: selectedVoice.provider || 'aws_polly',
              }
            : {}),
          video_included: true,
          secondary_camera_enabled: isSecondaryCameraEnabled,
          ...(liveAvatarEnabled && selectedAvatarVoice?.id
            ? { live_avatar_voice_id: selectedAvatarVoice.id }
            : {}),
        });

        const whitelabelData = {
          whitelabel_enabled: effectiveWhitelabelEnabled,
          whitelabel_config: {
            company_display_name: companyDisplayName,
            contact_email: contactEmail,
            pdf_watermark_text: companyDisplayName,
            platform_url: null,
            ats_subdomain: atsSubdomain || null,
            logo_primary: logoPrimaryPreview || null,
            logo_primary_dark: logoPrimaryDarkPreview || null,
            logo_compact: logoCompactPreview || null,
            logo_black: logoPrimaryPreview || null,
            logo_white: logoPrimaryPreview || null,
            logo_demo_black: logoPrimaryPreview || null,
            logo_demo: logoPrimaryPreview || null,
            logo_homepage: logoPrimaryPreview || null,
            logo_email: logoPrimaryPreview || null,
          },
        };

        await saveWhitelabelConfigService(whitelabelData);

        clearBrandingCache();
        sessionStorage.clear();

        if (data.status === 200) {
          toast.success('Configuration saved successfully!');
          // Reset baseline to current values after successful save
          setInitialConfig({
            resumeScoreThreshold,
            interviewScoreThreshold,
            selectedLanguage,
            voice: selectedVoice
              ? { ...selectedVoice }
              : { name: null, type: null, provider: 'aws_polly' },
            isSecondaryCameraEnabled,
            whitelabelEnabled: effectiveWhitelabelEnabled,
            companyDisplayName,
            contactEmail,
            atsSubdomain,
            logoPrimaryPreview,
            logoPrimaryDarkPreview,
            logoCompactPreview,
            liveAvatarEnabled,
            liveAvatarVoiceId: selectedAvatarVoice?.id || null,
          });
        }
      } catch (error) {
        toast.error('Failed to save configuration!');
        console.error('Error:', error);
      } finally {
        setIsSaving(false);
      }
    },
    [
      resumeScoreThreshold,
      interviewScoreThreshold,
      selectedLanguage,
      selectedVoice,
      isSecondaryCameraEnabled,
      whitelabelEnabled,
      companyDisplayName,
      contactEmail,
      atsSubdomain,
      logoPrimaryPreview,
      logoPrimaryDarkPreview,
      logoCompactPreview,
      liveAvatarEnabled,
      selectedAvatarVoice,
      isSaving,
    ],
  );

  // Memoized voice items to prevent unnecessary re-renders
  const voiceItems = useMemo(() => {
    // Only show skeleton during manual language changes (not initial load)
    if (isVoicesLoading) {
      return [1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="dark:glass-effect bg-bgPrimary p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <SkeletonLoader className="w-4 h-4 rounded-full" />
              <div>
                <SkeletonLoader className="h-4 w-24 mb-2" />
                <SkeletonLoader className="h-3 w-16" />
              </div>
            </div>
            <SkeletonLoader className="w-8 h-8 rounded-full" />
          </div>
        </div>
      ));
    }

    if (!voices || voices.length === 0) {
      return (
        <div className="col-span-full text-center text-[#D8D8D8] py-8">
          No voices available for this language.
        </div>
      );
    }

    return voices.map((voice, index) => (
      <div key={`${voice.name}-${voice.provider}`} className="relative">
        <label className="dark:glass-effect bg-bgPrimary block p-4 rounded-lg cursor-pointer dark:hover:bg-white/5 hover:bg-gray-200 dark:border-0 border-2 transition-all duration-200">
          <input
            type="radio"
            name="selectedVoice"
            checked={
              selectedVoice?.name === voice.name &&
              selectedVoice?.provider === voice.provider
            }
            onChange={() => {
              handlePlayAudio(voice);
            }}
            className="absolute opacity-0"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div
                className={`w-4 h-4 rounded-full transition-all ${
                  selectedVoice?.name === voice.name &&
                  selectedVoice?.provider === voice.provider
                    ? 'bg-blue-500 ring-2 ring-blue-300 ring-offset-2 ring-offset-gray-800'
                    : 'bg-gray-500'
                }`}
              />
              <div className="dark:text-[#D9D9D9] text-textPrimary">
                <span className="font-medium block text-sm">
                  {voice.display_name || voice.name}
                </span>
                <div className="flex items-center gap-2 mt-1">
                  {voice.provider !== 'google_gemini' && (
                    <span className="text-xs dark:text-[#D8D8D8] text-textSecondary">
                      {voice.type}
                    </span>
                  )}
                  {voice.provider === 'google_gemini' && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-300 border border-emerald-400/30 font-semibold">
                      NEW
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              className="flex items-center justify-center w-8 h-8 rounded-full dark:bg-[#FB54AE] dark:hover:bg-[#e84c9c] transition-colors bg-gradient-to-r from-pink-500 to-blue-500
"
              onClick={(e) => {
                e.preventDefault();
                handlePlayAudio(voice);
              }}
            >
              {selectedVoice?.name === voice.name &&
              selectedVoice?.provider === voice.provider &&
              isPlaying ? (
                <Pause className="w-4 h-4 text-white" />
              ) : (
                <Play className="w-4 h-4 text-white" />
              )}
            </button>
          </div>
        </label>
      </div>
    ));
  }, [voices, isVoicesLoading, selectedVoice, isPlaying]);

  // Shared audio player — stops any current audio then plays url.
  const playAudio = useCallback(
    async (url, onPlay, onStop) => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      const newAudio = new Audio(url);
      newAudio.onended = onStop;
      newAudio.onerror = () => {
        onStop();
        toast.error('Audio sample not available yet');
      };
      setAudio(newAudio);
      try {
        await newAudio.play();
        onPlay();
      } catch {
        onStop();
      }
    },
    [audio],
  );

  const handlePlayAudio = useCallback(
    async (voice) => {
      if (
        selectedVoice?.name === voice.name &&
        selectedVoice?.provider === voice.provider &&
        isPlaying &&
        audio
      ) {
        audio.pause();
        audio.currentTime = 0;
        setIsPlaying(false);
        return;
      }
      setIsPlaying(false);
      setAvatarPlayingId(null);
      setSelectedVoice(voice);
      const fileName =
        voice.provider === 'google_gemini'
          ? `gemini_${voice.name.toLowerCase().replace(/[^a-z0-9]/g, '_')}.mp3`
          : `${voice.name}.mp3`;
      const sampleUrl =
        voice.sample_url ||
        `https://${BUCKET_NAME}.s3.${AWS_REGION_NAME}.amazonaws.com/Voices/${fileName}`;
      await playAudio(
        sampleUrl,
        () => setIsPlaying(true),
        () => setIsPlaying(false),
      );
    },
    [selectedVoice, isPlaying, audio, playAudio, BUCKET_NAME, AWS_REGION_NAME],
  );

  const handlePlayAvatarVoice = useCallback(
    async (voice) => {
      if (avatarPlayingId === voice.id && audio) {
        audio.pause();
        audio.currentTime = 0;
        setAvatarPlayingId(null);
        return;
      }
      setAvatarPlayingId(null);
      setIsPlaying(false);
      setSelectedAvatarVoice(voice);
      await playAudio(
        `https://${BUCKET_NAME}.s3.${AWS_REGION_NAME}.amazonaws.com/Voices/la_${voice.id}.mp3`,
        () => setAvatarPlayingId(voice.id),
        () => setAvatarPlayingId(null),
      );
    },
    [avatarPlayingId, audio, playAudio, BUCKET_NAME, AWS_REGION_NAME],
  );

  const avatarVoiceItems = useMemo(
    () =>
      AVATAR_VOICES.map((voice) => (
        <div key={voice.id} className="relative">
          <label className="dark:glass-effect bg-bgPrimary block p-4 rounded-lg cursor-pointer dark:hover:bg-white/5 hover:bg-gray-200 dark:border-0 border-2 transition-all duration-200">
            <input
              type="radio"
              name="selectedAvatarVoice"
              checked={selectedAvatarVoice?.id === voice.id}
              onChange={() => handlePlayAvatarVoice(voice)}
              className="absolute opacity-0"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div
                  className={`w-4 h-4 rounded-full flex-shrink-0 transition-all ${
                    selectedAvatarVoice?.id === voice.id
                      ? 'bg-blue-500 ring-2 ring-blue-300 ring-offset-2 ring-offset-gray-800'
                      : 'bg-gray-500'
                  }`}
                />
                <div className="dark:text-[#D9D9D9] text-textPrimary">
                  <span className="font-medium block text-sm">
                    {voice.name}
                  </span>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-xs dark:text-[#D8D8D8] text-textSecondary capitalize">
                      {voice.gender}
                    </span>
                    {voice.style && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-purple-500/20 dark:text-purple-300 text-purple-600 border border-purple-400/30">
                        {voice.style}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                className="flex items-center justify-center w-8 h-8 rounded-full dark:bg-[#FB54AE] dark:hover:bg-[#e84c9c] transition-colors bg-gradient-to-r from-pink-500 to-blue-500"
                onClick={(e) => {
                  e.preventDefault();
                  handlePlayAvatarVoice(voice);
                }}
              >
                {avatarPlayingId === voice.id ? (
                  <Pause className="w-4 h-4 text-white" />
                ) : (
                  <Play className="w-4 h-4 text-white" />
                )}
              </button>
            </div>
          </label>
        </div>
      )),
    [selectedAvatarVoice, avatarPlayingId, handlePlayAvatarVoice],
  );

  // Loading state for entire page
  if (isLoading) {
    return (
      <div className="min-h-screen dark:glass-effect bg-bgSecondary rounded-2xl">
        <div className="table-container">
          <div className="table-header px-6">
            <div className="flex items-center justify-between py-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="dark:text-[#D8D8D8] dark:hover:text-[#D9D9D9] text-textPrimary hover:text-textSecondary transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-xl font-normal dark:text-[#D8D8D8] text-textPrimary">
                  Interview Configurations
                </h1>
              </div>
            </div>
          </div>

          <div className="px-6 pb-8">
            {/* Tabs Skeleton */}
            <div className="flex items-center mt-4 relative z-10">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={`px-6 py-3 border-t border-l border-r dark:border-[#5A4479A3] border-gray-300 rounded-t-[11px] ${
                    i === 1
                      ? 'bg-white dark:glass-effect z-20 -mb-[1px] border-b-white dark:border-b-indigo-950'
                      : 'dark:glass-effect bg-gray-50 opacity-40'
                  }`}
                >
                  <SkeletonLoader className="h-4 w-28" />
                </div>
              ))}
            </div>

            {/* Content Box Skeleton — matches Company Profile tab */}
            <div className="px-8 pt-4 pb-8 bg-white dark:glass-effect border dark:border-[#5A4479A3] border-gray-300 rounded-tr-xl rounded-b-xl relative z-0">
              {/* Section header */}
              <div className="pb-4">
                <SkeletonLoader className="h-5 w-40 mb-2" />
                <SkeletonLoader className="h-3 w-72" />
              </div>
              {/* Rows */}
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="grid py-5 border-b border-gray-100 dark:border-white/5" style={{ gridTemplateColumns: '220px 1fr', gap: '16px 40px' }}>
                  <SkeletonLoader className="h-4 w-32" />
                  <SkeletonLoader className="h-10 w-full rounded-md" />
                </div>
              ))}
              {/* About Us section header */}
              <div className="pt-7 pb-4">
                <SkeletonLoader className="h-5 w-24 mb-2" />
                <SkeletonLoader className="h-3 w-80" />
              </div>
              {/* Rich text editor placeholder */}
              <SkeletonLoader className="h-40 w-full rounded-md" />
              {/* HR section header */}
              <div className="pt-7 pb-4">
                <SkeletonLoader className="h-5 w-48 mb-2" />
                <SkeletonLoader className="h-3 w-64" />
              </div>
              {[1, 2].map((i) => (
                <div key={i} className="grid py-5 border-b border-gray-100 dark:border-white/5" style={{ gridTemplateColumns: '220px 1fr', gap: '16px 40px' }}>
                  <SkeletonLoader className="h-4 w-32" />
                  <SkeletonLoader className="h-10 w-full rounded-md" />
                </div>
              ))}
              {/* Action buttons */}
              <div className="flex justify-end gap-3 pt-6 mt-2 border-t border-gray-100 dark:border-white/5">
                <SkeletonLoader className="h-9 w-20 rounded-full" />
                <SkeletonLoader className="h-9 w-28 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen dark:glass-effect bg-bgSecondary rounded-2xl">
      <div className="table-container">
        <div className="table-header px-6">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="dark:text-[#D8D8D8] dark:hover:text-[#D9D9D9] text-textPrimary hover:text-textSecondary transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-normal dark:text-[#D8D8D8] text-textPrimary">
                  Interview Configurations
                </h1>
                {/* <ChevronRight className="w-4 h-4 dark:text-[#D8D8D8] text-textSecondary" /> */}
                {/* <span className="text-xl dark:text-[#D8D8D8] text-textPrimary font-normal">
                  {CONFIG_BOXES.find((b) => b.id === selectedBox)?.title}
                </span> */}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 pb-8">
          <style>{`
            .tab-active { z-index: 20; }
            .tab-active::before {
              content: ''; position: absolute; bottom: -1px; left: -12px; width: 12px; height: 13px;
              background: radial-gradient(circle at 0 0, #f8f9ff 0, #f8f9ff 11.5px, #d1d5db 11.5px, #d1d5db 12.5px, white 12.5px);
            }
            .tab-active::after, .tab-active-first::after {
              content: ''; position: absolute; bottom: -1px; right: -12px; width: 12px; height: 13px;
              background: radial-gradient(circle at 100% 0, #f8f9ff 0, #f8f9ff 11.5px, #d1d5db 11.5px, #d1d5db 12.5px, white 12.5px);
            }
            .dark .tab-active {
              background: 
                linear-gradient(#5A4479A3, #5A4479A3) right top / 1px calc(100% - 9px) no-repeat,
                linear-gradient(#5A4479A3, #5A4479A3) left top / 1px calc(100% - 9px) no-repeat,
                rgba(105, 13, 197, 0.103) !important;
            }
            .dark .tab-active-first {
              background: 
                linear-gradient(#5A4479A3, #5A4479A3) right top / 1px calc(100% - 9px) no-repeat,
                linear-gradient(#5A4479A3, #5A4479A3) left top / 1px 100% no-repeat,
                rgba(105, 13, 197, 0.103) !important;
            }
            .dark .tab-active::before {
              content: ''; position: absolute; bottom: -1px; left: -12px; width: 12px; height: 13px;
              background: radial-gradient(circle at 0 0, transparent 0, transparent 11.5px, #5A4479A3 11.5px, #5A4479A3 12.5px, transparent 12.5px);
            }
            .dark .tab-active::after, .dark .tab-active-first::after {
              content: ''; position: absolute; bottom: -1px; right: -12px; width: 12px; height: 13px;
              background: radial-gradient(circle at 100% 0, transparent 0, transparent 11.5px, #5A4479A3 11.5px, #5A4479A3 12.5px, transparent 12.5px);
            }
          `}</style>
          <div className="flex pb-0 mt-4 items-end justify-start w-full relative z-10">
            <div className="flex items-end">
              {CONFIG_BOXES.map((box, index) => {
                const isSelected = selectedBox === box.id;
                return (
                  <div
                    key={box.id}
                    onClick={() => {
                      if (hasChanges && selectedBox !== box.id) {
                        const currentTabName = CONFIG_BOXES.find(
                          (b) => b.id === selectedBox,
                        )?.title;
                        toast.warn(
                          `Please save the changes made in the "${currentTabName}" section before switching tabs.`,
                        );
                        return;
                      }
                      setSelectedBox(box.id);
                    }}
                    className={`px-6 cursor-pointer relative ${
                      index === 0
                        ? isSelected ? 'ml-0 mr-0.5 dark:mr-[12px]' : 'ml-0 mr-0.5 dark:mr-0'
                        : isSelected ? 'mx-0.5 dark:ml-[12px] dark:mr-[12px]' : 'mx-0.5 dark:mx-0'
                    } ${
                      isSelected
                        ? `bg-white py-3 border-t-[1px] border-l-[1px] border-r-[1px] border-gray-300 dark:border-transparent dark:border-t-[#5A4479A3] -mb-[1px] border-b-[1px] border-b-white dark:border-b-transparent z-20 ${index === 0 ? 'tab-active-first rounded-t-[11px]' : 'tab-active rounded-t-[11px]'}`
                        : 'py-3 border-b-[1px] border-b-transparent z-10'
                    }`}
                  >
                    <span
                      className={`block relative text-sm
                        ${
                          isSelected
                            ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                            : 'text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
                        }
                        before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
                      `}
                      data-text={box.title}
                    >
                      {box.title}
                    </span>
                    {/* The geometrically perfect piecewise horizontal line used ONLY for inactive tabs */}
                    {!isSelected && (
                      <div className={`absolute bottom-[-1px] right-[0] h-[1px] bg-[#5A4479A3] hidden dark:block z-10 ${index === 0 ? 'left-[12px]' : 'left-[0]'}`}></div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* The piecewise horizontal filler line, which purposefully stops 12px short of the edge to smoothly trace the rounded right corner curve */}
            <div className="flex-1 hidden dark:block w-full border-b-[1px] border-b-transparent dark:border-b-[#5A4479A3] mr-[12px]"></div>
          </div>

          {selectedBox && (
            <div
              className={`space-y-8 mt-0 bg-white dark:glass-effect border-[1px] border-gray-300 dark:border-[#5A4479A3] dark:border-t-transparent relative z-0 ${selectedBox === 'company_profile' ? 'px-8 pt-4 pb-8 rounded-tr-xl rounded-b-xl rounded-tl-none' : 'p-8 rounded-xl'}`}
            >
              {/* ══ COMPANY PROFILE ══ */}
              {selectedBox === 'company_profile' && (
                <EditCompanyProfile
                  initialData={companyProfileData}
                  onSaved={(saved) => setCompanyProfileData((prev) => ({ ...(prev || {}), ...saved }))}
                />
              )}

              {/* ══ INTERVIEW CONFIGURATIONS ══ */}
              {selectedBox === 'interview_config' && (
                <>
                  <div className="space-y-8">
                    <div className="space-y-6">
                      {/* ── Threshold Cards ── */}
                      <div className="flex flex-wrap gap-2">
                        {/* Resume Score Threshold */}
                        <div className="dark:glass-effect bg-bgPrimary dark:border-0 border-2 border-gray-200 py-2.5 px-4 rounded-lg transition max-w-xs w-full sm:w-auto flex-1 min-w-[280px]">
                          <label className="text-[11px] tracking-[0.15em] uppercase dark:text-[#D9D9D9] text-textPrimary font-semibold">
                            Resume Score Threshold
                          </label>

                          <div className="flex items-end gap-2 mt-2">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={resumeScoreThreshold}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') {
                                  setResumeScoreThreshold('');
                                  return;
                                }
                                const val = Math.max(
                                  0,
                                  Math.min(100, parseInt(raw, 10)),
                                );
                                setResumeScoreThreshold(val);
                              }}
                              onBlur={() => {
                                if (
                                  resumeScoreThreshold === '' ||
                                  isNaN(resumeScoreThreshold)
                                )
                                  setResumeScoreThreshold(0);
                              }}
                              style={{
                                fontSize: '32px',
                                fontWeight: 600,
                                lineHeight: 1,
                                width:
                                  String(resumeScoreThreshold).length >= 3
                                    ? '120px'
                                    : '85px',
                                transition: 'width 0.15s ease',
                                padding: 0,
                                margin: 0,
                              }}
                              className="bg-transparent text-center dark:!text-[#D8D8D8] !text-textSecondary outline-none border-0 border-b-[3px] border-[#7c6be4] focus:border-[#7c6be4] focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <span className="text-[20px] dark:text-[#D8D8D8] text-textSecondary font-medium pb-2">
                              /100
                            </span>
                          </div>

                          <div className="mt-6">
                            <Slider
                              min={0}
                              max={100}
                              value={
                                typeof resumeScoreThreshold === 'number'
                                  ? resumeScoreThreshold
                                  : 0
                              }
                              onChange={(val) => setResumeScoreThreshold(val)}
                              trackStyle={{
                                background:
                                  'linear-gradient(to right, #6366f1, #ec4899)',
                                height: 4,
                                borderRadius: 999,
                              }}
                              handleStyle={{
                                borderColor: '#6366f1',
                                backgroundColor: '#ffffff',
                                opacity: 1,
                                boxShadow: '0 0 0 2px white',
                                width: 14,
                                height: 14,
                                marginTop: -5,
                              }}
                              railStyle={{
                                backgroundColor:
                                  theme === 'dark'
                                    ? 'rgba(255,255,255,0.15)'
                                    : '#e5e7eb',
                                height: 4,
                                borderRadius: 999,
                              }}
                            />
                          </div>

                          <p className="text-xs dark:text-[#D8D8D8] text-textSecondary mt-3">
                            Click the number to type, or drag the bar
                          </p>
                        </div>

                        {/* Interview Score Threshold */}
                        <div className="dark:glass-effect bg-bgPrimary dark:border-0 border-2 border-gray-200 py-2.5 px-4 rounded-lg transition max-w-xs w-full sm:w-auto flex-1 min-w-[280px]">
                          <label className="text-[11px] tracking-[0.15em] uppercase dark:text-[#D9D9D9] text-textPrimary font-semibold">
                            Interview Score Threshold
                          </label>

                          <div className="flex items-end gap-2 mt-2">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={interviewScoreThreshold}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') {
                                  setInterviewScoreThreshold('');
                                  return;
                                }
                                const val = Math.max(
                                  0,
                                  Math.min(100, parseInt(raw, 10)),
                                );
                                setInterviewScoreThreshold(val);
                              }}
                              onBlur={() => {
                                if (
                                  interviewScoreThreshold === '' ||
                                  isNaN(interviewScoreThreshold)
                                )
                                  setInterviewScoreThreshold(0);
                              }}
                              style={{
                                fontSize: '32px',
                                fontWeight: 600,
                                lineHeight: 1,
                                width:
                                  String(interviewScoreThreshold).length >= 3
                                    ? '120px'
                                    : '85px',
                                transition: 'width 0.15s ease',
                                padding: 0,
                                margin: 0,
                              }}
                              className="bg-transparent text-center dark:!text-[#D8D8D8] !text-textSecondary outline-none border-0 border-b-[3px] border-[#7c6be4] focus:border-[#7c6be4] focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <span className="text-[20px] dark:text-[#D8D8D8] text-textSecondary font-medium pb-2">
                              /100
                            </span>
                          </div>

                          <div className="mt-6">
                            <Slider
                              min={0}
                              max={100}
                              value={
                                typeof interviewScoreThreshold === 'number'
                                  ? interviewScoreThreshold
                                  : 0
                              }
                              onChange={(val) =>
                                setInterviewScoreThreshold(val)
                              }
                              trackStyle={{
                                background:
                                  'linear-gradient(to right, #6366f1, #ec4899)',
                                height: 4,
                                borderRadius: 999,
                              }}
                              handleStyle={{
                                borderColor: '#6366f1',
                                backgroundColor: '#ffffff',
                                opacity: 1,
                                boxShadow: '0 0 0 2px white',
                                width: 14,
                                height: 14,
                                marginTop: -5,
                              }}
                              railStyle={{
                                backgroundColor:
                                  theme === 'dark'
                                    ? 'rgba(255,255,255,0.15)'
                                    : '#e5e7eb',
                                height: 4,
                                borderRadius: 999,
                              }}
                            />
                          </div>

                          <p className="text-xs dark:text-[#D8D8D8] text-textSecondary mt-3">
                            Click the number to type, or drag the bar
                          </p>
                        </div>

                        {/* ── Interview Language ── */}
                        <div className="dark:glass-effect bg-bgPrimary dark:border-0 border-2 border-gray-200 py-2.5 px-4 rounded-lg space-y-2 transition max-w-xs w-full sm:w-auto flex-1 min-w-[280px]">
                          <label className="text-[11px] tracking-[0.15em] uppercase dark:text-[#D9D9D9] text-textPrimary font-semibold">
                            Interview Language
                          </label>

                          <div className="relative w-full">
                            <button
                              type="button"
                              onClick={() =>
                                setShowLanguageDropdown(!showLanguageDropdown)
                              }
                              className="w-full px-3 py-2 rounded-md border dark:border-white/15 border-gray-300 bg-white/5 flex items-center justify-between hover:bg-white/10 transition"
                            >
                              <span className="text-sm dark:text-[#D9D9D9] text-textPrimary">
                                {selectedLanguage || 'Select Language'}
                              </span>
                              <ChevronDown className="w-4 h-4 dark:text-white/60 text-textSecondary" />
                            </button>

                            {showLanguageDropdown && (
                              <div className="absolute top-full left-0 right-0 mt-1 rounded-md border dark:border-white/10 border-gray-300 dark:bg-[#140D2A] bg-white backdrop-blur-md shadow-lg z-50 max-h-52 overflow-y-auto">
                                {languages.map((lang, index) => (
                                  <button
                                    key={index}
                                    onClick={() => {
                                      handleLanguageChange(lang);
                                      setShowLanguageDropdown(false);
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm dark:text-white/80 text-textPrimary hover:bg-white/10 transition"
                                  >
                                    {lang}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          <p className="text-[11px] dark:text-[#D8D8D8] text-textSecondary flex items-center gap-1 pt-10">
                            <Info size={10} /> Language used by the AI
                            interviewer
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Voice Selection (hidden when live avatar is enabled — avatar has its own voice section) */}
                  {selectedLanguage && !liveAvatarEnabled && (
                    <div className="space-y-6">
                      <h2 className="text-lg font-medium dark:text-[#D9D9D9] text-textSecondary">
                        Voice Selection
                      </h2>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {voiceItems}
                      </div>
                    </div>
                  )}
                  {/* Avatar Interview Voice (only visible when live_avatar_enabled) */}
                  {liveAvatarEnabled && (
                    <div className="space-y-4">
                      <div>
                        <h2 className="text-lg font-medium dark:text-[#D9D9D9] text-textSecondary">
                          Avatar Interview Voice
                        </h2>
                        <p className="text-sm dark:text-[#D8D8D8] text-textSecondary mt-1">
                          Voice used when interviews run as AI avatar sessions
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {avatarVoiceItems}
                      </div>
                    </div>
                  )}

                  {/* Interview Monitoring Settings */}
                  <div className="pt-8 border-t dark:border-white/10 border-gray-200 space-y-4">
                    <h2 className="text-lg font-medium dark:text-[#D9D9D9] text-textSecondary">
                      Monitoring Settings
                    </h2>
                    <div className="dark:glass-effect bg-bgPrimary dark:border-0 border-2 border-gray-200 p-6 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="dark:text-[#D9D9D9] text-textPrimary font-medium">
                              Enable Video Proctoring
                            </h3>
                            <span className="px-2 py-0.5 bg-blue-500/20 dark:text-blue-300 text-blue-600 text-xs rounded-full border border-blue-400/30 font-semibold">
                              BETA
                            </span>
                          </div>
                          <p className="dark:text-[#D8D8D8] text-textSecondary text-sm">
                            Monitor candidate behavior through primary camera
                            during interview
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isVideoIncluded}
                            onChange={(e) => {
                              setIsVideoIncluded(e.target.checked);
                              if (!e.target.checked) {
                                setIsSecondaryCameraEnabled(false);
                              }
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ══ WHITE LABELLING ══ */}
              {selectedBox === 'whitelabel' && (
                <>
                  <div className="space-y-4">
                    <div className="dark:glass-effect bg-bgPrimary dark:border-0 border-gray-200 border-2 p-6 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="dark:text-white text-black font-medium">
                              Enable White-Labeling
                            </h3>
                            <span className="px-2 py-0.5 bg-purple-500/20 dark:text-purple-300 text-purple-600 text-xs rounded-full border border-purple-400/30 font-semibold">
                              PREMIUM
                            </span>
                          </div>
                          <p className="dark:text-[#D8D8D8] text-textSecondary text-sm">
                            Show your company branding to candidates instead of
                            GenieHire
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={whitelabelEnabled}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setWhitelabelEnabled(isChecked);
                              if (!isChecked) {
                                handleSaveGeneralConfig(false);
                              }
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                        </label>
                      </div>
                    </div>

                    {whitelabelEnabled && (
                      <div className="space-y-6">
                        {/* Company Display Name */}
                        <div className="dark:glass-effect bg-bgPrimary dark:border-0 border-gray-200 border-2 p-6 rounded-lg">
                          <label className="text-sm font-medium dark:text-[#D8D8D8] text-textSecondary block mb-2">
                            Company Display Name
                          </label>
                          <input
                            type="text"
                            value={companyDisplayName}
                            onChange={(e) =>
                              setCompanyDisplayName(e.target.value)
                            }
                            className="w-full px-4 py-2.5 rounded-lg outline-none transition
    bg-bgPrimary dark:glass-effect
    !text-black dark:!text-white
    placeholder:text-gray-400 dark:placeholder:text-white/40
    border border-gray-300 dark:border-white/10
    focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Your Company Name"
                          />
                          <p className="text-xs dark:text-[#D8D8D8] text-textSecondary mt-2">
                            <span className="dark:text-purple-300 text-purple-500">
                              ✓ Auto-filled from your organization profile.
                            </span>{' '}
                            You can edit this name if needed. It will appear to
                            candidates in emails, application pages, and as
                            watermark on PDF reports.
                          </p>
                        </div>

                        {/* Contact Information */}
                        <div className="dark:glass-effect bg-bgPrimary dark:border-0 border-gray-200 border-2 p-6 rounded-lg">
                          <h4 className="dark:text-white text-black font-medium mb-4">
                            Contact Information
                          </h4>
                          <div>
                            <label className="text-sm dark:text-[#D8D8D8] text-textSecondary block mb-2">
                              Support Email (Optional)
                            </label>
                            <input
                              type="email"
                              value={contactEmail}
                              onChange={(e) => setContactEmail(e.target.value)}
                              className="w-full px-4 py-2.5 rounded-lg outline-none transition
    bg-bgPrimary dark:glass-effect
    !text-black dark:!text-white
    placeholder:text-gray-400 dark:placeholder:text-white/40
    border border-gray-300 dark:border-white/10
    focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                              placeholder="support@yourcompany.com"
                            />
                            <p className="text-xs dark:text-[#D8D8D8] text-textSecondary mt-2">
                              This email will appear in candidate communications
                            </p>
                          </div>
                        </div>

                        {/* ATS Subdomain */}
                        <div className="dark:glass-effect bg-bgPrimary dark:border-0 border-gray-200 border-2 p-6 rounded-lg">
                          <label className="text-sm font-medium dark:text-[#D8D8D8] text-textSecondary block mb-2">
                            ATS Link Subdomain
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={atsSubdomain}
                              onChange={(e) =>
                                setAtsSubdomain(
                                  e.target.value
                                    .toLowerCase()
                                    .replace(/[^a-z0-9-]/g, ''),
                                )
                              }
                              className="w-64 px-4 py-2.5 rounded-lg outline-none transition
    bg-bgPrimary dark:glass-effect
    !text-black dark:!text-white
    placeholder:text-gray-400 dark:placeholder:text-white/40
    border border-gray-300 dark:border-white/10
    focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                              placeholder="yourcompany"
                            />
                            <span className="dark:text-[#D8D8D8] text-textSecondary text-sm">
                              .{window.location.host}
                            </span>
                          </div>
                          {atsSubdomain && (
                            <p className="text-xs dark:text-purple-300 text-purple-500 mt-2">
                              Preview:{' '}
                              <span className="font-medium">
                                {atsSubdomain}.{window.location.host}/apply/...
                              </span>
                            </p>
                          )}
                          <p className="text-xs dark:text-[#D8D8D8] text-textSecondary mt-2">
                            Your company subdomain for ATS application links.
                            Only lowercase letters allowed.
                          </p>
                        </div>

                        {/* PRIMARY LOGO */}
                        <div className="dark:glass-effect bg-bgPrimary dark:border-0 border-gray-200 border-2 p-6 rounded-lg space-y-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="dark:text-white text-black font-medium">
                                Primary Logo
                              </h4>
                              <span className="px-2 py-0.5 bg-red-500/20 dark:text-red-300 text-red-600 text-xs rounded-full border border-red-400/30 font-semibold">
                                REQUIRED
                              </span>
                            </div>
                            <p className="text-xs dark:text-[#D8D8D8] text-textSecondary">
                              Used in emails, PDF reports, and headers.
                            </p>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="dark:bg-gray-800/50 bg-gray-100 dark:border-gray-700 border-gray-300 border rounded-lg p-3">
                              <p className="text-xs dark:text-purple-300 text-purple-600 mb-2 font-medium">
                                ✓ Good Example:
                              </p>
                              <div className="flex items-center gap-4">
                                <div
                                  className="bg-white rounded p-3"
                                  style={{
                                    width: '180px',
                                    height: '60px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <img
                                    src="/GenieHire-logo-black.png"
                                    alt="Good"
                                    style={{
                                      maxHeight: '100%',
                                      maxWidth: '100%',
                                      objectFit: 'contain',
                                    }}
                                  />
                                </div>
                                <div className="flex-1">
                                  <p className="text-xs dark:text-gray-300 text-gray-700">
                                    ✓ Wide horizontal
                                    <br />✓ Readable in emails
                                    <br />✓ Professional
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="dark:bg-gray-800/50 bg-gray-100 dark:border-gray-700 border-gray-300 border rounded-lg p-3">
                              <p className="text-xs dark:text-red-300 text-red-600 mb-2 font-medium">
                                ✗ Bad Example:
                              </p>
                              <div className="flex items-center gap-4">
                                <div
                                  className="bg-white rounded p-3"
                                  style={{
                                    width: '180px',
                                    height: '60px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <img
                                    src="/Favicon.png"
                                    alt="Bad"
                                    style={{
                                      maxHeight: '100%',
                                      maxWidth: '40px',
                                      objectFit: 'contain',
                                    }}
                                  />
                                </div>
                                <div className="flex-1">
                                  <p className="text-xs dark:text-gray-300 text-gray-700">
                                    ✗ Too tall/square
                                    <br />✗ Tiny in emails
                                    <br />✗ Wastes space
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-4">
                            <label className="cursor-pointer w-fit">
                              <div className="px-4 py-2 bg-purple-500/20 dark:text-purple-300 text-purple-600 rounded-lg hover:bg-purple-500/30 transition-colors flex items-center gap-2 border border-purple-400/30">
                                {isUploadingPrimary ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="text-sm">
                                      Uploading...
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4" />
                                    <span className="text-sm">
                                      Upload Primary Logo
                                    </span>
                                  </>
                                )}
                              </div>
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg"
                                onChange={(e) => handleLogoUpload(e, 'primary')}
                                className="hidden"
                                disabled={isUploadingPrimary}
                              />
                            </label>
                            {logoPrimaryPreview && (
                              <div className="flex items-start gap-3">
                                <div
                                  className="p-3 rounded-lg dark:border-gray-600 border-gray-400 border"
                                  style={{
                                    background:
                                      'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 50% / 20px 20px',
                                  }}
                                >
                                  <img
                                    src={logoPrimaryPreview}
                                    alt="Primary Logo Preview"
                                    className="max-w-[200px] max-h-[80px] object-contain"
                                  />
                                </div>
                                <div className="flex-1">
                                  {logoPrimaryDimensions && (
                                    <p className="text-xs dark:text-[#D8D8D8] text-textSecondary mb-2">
                                      <span className="dark:text-purple-300 text-purple-600">
                                        Dimensions:
                                      </span>{' '}
                                      {logoPrimaryDimensions.width}×
                                      {logoPrimaryDimensions.height}px
                                    </p>
                                  )}
                                  <button
                                    onClick={() => handleRemoveLogo('primary')}
                                    className="px-3 py-1 text-xs dark:text-red-400 text-red-600 dark:hover:text-red-300 hover:text-red-500 border border-red-400/30 rounded hover:bg-red-500/10 transition-colors flex items-center gap-1"
                                  >
                                    <X className="w-3 h-3" />
                                    Remove
                                  </button>
                                </div>
                              </div>
                            )}
                            {!logoPrimaryPreview && (
                              <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-400/30 rounded-lg">
                                <AlertCircle className="w-4 h-4 dark:text-red-300 text-red-600 flex-shrink-0 mt-0.5" />
                                <p className="text-xs dark:text-red-300 text-red-600">
                                  You must upload a primary logo before saving
                                  white-label configuration.
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="text-xs dark:text-gray-400 text-gray-500 space-y-1">
                            <p>
                              • <strong>Recommended:</strong> 400×100px to
                              800×200px
                            </p>
                            <p>
                              • <strong>Minimum:</strong> 100×30px |{' '}
                              <strong>Maximum:</strong> 2000×800px
                            </p>
                            <p>
                              • <strong>Format:</strong> PNG or JPG (transparent
                              PNG recommended)
                            </p>
                            <p>
                              • <strong>Max file size:</strong> 2MB
                            </p>
                          </div>
                        </div>

                        {/* PRIMARY LOGO (DARK) */}
                        <div className="dark:glass-effect bg-bgPrimary dark:border-0 border-gray-200 border-2 p-6 rounded-lg space-y-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="dark:text-white text-black font-medium">
                                Primary Logo (Dark Theme)
                              </h4>
                              <span className="px-2 py-0.5 bg-gray-500/20 dark:text-gray-300 text-gray-600 text-xs rounded-full border dark:border-gray-400/30 border-gray-400/50 font-semibold">
                                OPTIONAL
                              </span>
                            </div>
                            <p className="text-xs dark:text-[#D8D8D8] text-textSecondary">
                              Alternative logo for dark theme backgrounds. If
                              not provided, the light theme logo will be used.
                            </p>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="dark:bg-gray-800/50 bg-gray-800/50 dark:border-gray-700 border-gray-300 border rounded-lg p-3">
                              <p className="text-xs dark:text-purple-300 text-purple-600 mb-2 font-medium">
                                ✓ Good for Dark Theme:
                              </p>
                              <div className="flex items-center gap-4">
                                <div
                                  className="bg-gray-900 rounded p-3"
                                  style={{
                                    width: '180px',
                                    height: '60px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <img
                                    src="/genie-demo-logo.png"
                                    alt="Good Dark"
                                    style={{
                                      maxHeight: '100%',
                                      maxWidth: '100%',
                                      objectFit: 'contain',
                                    }}
                                  />
                                </div>
                                <div className="flex-1">
                                  <p className="text-xs dark:text-gray-300 text-gray-100">
                                    ✓ Light colored
                                    <br />✓ High contrast
                                    <br />✓ Visible on dark
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="dark:bg-gray-800/50 bg-gray-800/50 dark:border-gray-700 border-gray-300 border rounded-lg p-3">
                              <p className="text-xs dark:text-red-300 text-red-600 mb-2 font-medium">
                                ✗ Bad for Dark Theme:
                              </p>
                              <div className="flex items-center gap-4">
                                <div
                                  className="bg-gray-900 rounded p-3"
                                  style={{
                                    width: '180px',
                                    height: '60px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <img
                                    src="/GenieHire-logo-black.png"
                                    alt="Bad Dark"
                                    style={{
                                      maxHeight: '100%',
                                      maxWidth: '100%',
                                      objectFit: 'contain',
                                    }}
                                  />
                                </div>
                                <div className="flex-1">
                                  <p className="text-xs dark:text-gray-300 text-gray-100">
                                    ✗ Dark colored
                                    <br />✗ Low contrast
                                    <br />✗ Hard to see
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-4">
                            <label className="cursor-pointer w-fit">
                              <div className="px-4 py-2 bg-purple-500/20 dark:text-purple-300 text-purple-600 rounded-lg hover:bg-purple-500/30 transition-colors flex items-center gap-2 border border-purple-400/30">
                                {isUploadingPrimaryDark ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="text-sm">
                                      Uploading...
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4" />
                                    <span className="text-sm">
                                      Upload Dark Theme Logo
                                    </span>
                                  </>
                                )}
                              </div>
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg"
                                onChange={(e) =>
                                  handleLogoUpload(e, 'primary_dark')
                                }
                                className="hidden"
                                disabled={isUploadingPrimaryDark}
                              />
                            </label>
                            {logoPrimaryDarkPreview && (
                              <div className="flex items-start gap-3">
                                <div className="p-3 rounded-lg dark:border-gray-600 border-gray-400 border bg-gray-900">
                                  <img
                                    src={logoPrimaryDarkPreview}
                                    alt="Primary Dark Logo Preview"
                                    className="max-w-[200px] max-h-[80px] object-contain"
                                  />
                                </div>
                                <div className="flex-1">
                                  {logoPrimaryDarkDimensions && (
                                    <p className="text-xs dark:text-[#D8D8D8] text-textSecondary mb-2">
                                      <span className="dark:text-purple-300 text-purple-600">
                                        Dimensions:
                                      </span>{' '}
                                      {logoPrimaryDarkDimensions.width}×
                                      {logoPrimaryDarkDimensions.height}px
                                    </p>
                                  )}
                                  <button
                                    onClick={() =>
                                      handleRemoveLogo('primary_dark')
                                    }
                                    className="px-3 py-1 text-xs dark:text-red-400 text-red-600 dark:hover:text-red-300 hover:text-red-500 border border-red-400/30 rounded hover:bg-red-500/10 transition-colors flex items-center gap-1"
                                  >
                                    <X className="w-3 h-3" />
                                    Remove
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="text-xs dark:text-gray-400 text-gray-500 space-y-1">
                            <p>
                              • Same dimensions as light theme logo
                              (recommended: 400×100px to 800×200px)
                            </p>
                            <p>
                              • Use light colors that contrast well with dark
                              backgrounds
                            </p>
                            <p>
                              • If not uploaded, light theme logo will be used
                              everywhere
                            </p>
                          </div>
                        </div>

                        {/* COMPACT LOGO */}
                        <div className="dark:glass-effect bg-bgPrimary dark:border-0 border-gray-200 border-2 p-6 rounded-lg space-y-4">
                          <div>
                            <h4 className="dark:text-white text-black font-medium mb-2">
                              Compact Logo
                            </h4>
                            <p className="text-xs dark:text-[#D8D8D8] text-textSecondary">
                              Used for browser favicon/tab icon only.
                            </p>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="dark:bg-gray-800/50 bg-gray-100 dark:border-gray-700 border-gray-300 border rounded-lg p-3">
                              <p className="text-xs dark:text-purple-300 text-purple-600 mb-2 font-medium">
                                ✓ Good Example:
                              </p>
                              <div className="flex items-center gap-4">
                                <div
                                  className="bg-white rounded p-3"
                                  style={{
                                    width: '80px',
                                    height: '80px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <img
                                    src="/Favicon.png"
                                    alt="Good Square"
                                    style={{
                                      maxHeight: '100%',
                                      maxWidth: '100%',
                                      objectFit: 'contain',
                                    }}
                                  />
                                </div>
                                <div className="flex-1">
                                  <p className="text-xs dark:text-gray-300 text-gray-700">
                                    ✓ Square shape
                                    <br />✓ Clear at 16×16px
                                    <br />✓ Fits favicon
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="dark:bg-gray-800/50 bg-gray-100 dark:border-gray-700 border-gray-300 border rounded-lg p-3">
                              <p className="text-xs dark:text-red-300 text-red-600 mb-2 font-medium">
                                ✗ Bad Example:
                              </p>
                              <div className="flex items-center gap-4">
                                <div
                                  className="bg-white rounded p-3"
                                  style={{
                                    width: '80px',
                                    height: '80px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <img
                                    src="/GenieHire-logo-black.png"
                                    alt="Bad Horizontal"
                                    style={{
                                      maxWidth: '150%',
                                      objectFit: 'contain',
                                    }}
                                  />
                                </div>
                                <div className="flex-1">
                                  <p className="text-xs dark:text-gray-300 text-gray-700">
                                    ✗ Wide horizontal
                                    <br />✗ Unreadable tiny
                                    <br />✗ Cut off
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-4">
                            <label className="cursor-pointer w-fit">
                              <div className="px-4 py-2 bg-purple-500/20 dark:text-purple-300 text-purple-600 rounded-lg hover:bg-purple-500/30 transition-colors flex items-center gap-2 border border-purple-400/30">
                                {isUploadingCompact ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="text-sm">
                                      Uploading...
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4" />
                                    <span className="text-sm">
                                      Upload Compact Logo
                                    </span>
                                  </>
                                )}
                              </div>
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg"
                                onChange={(e) => handleLogoUpload(e, 'compact')}
                                className="hidden"
                                disabled={isUploadingCompact}
                              />
                            </label>
                            {logoCompactPreview && (
                              <div className="flex items-start gap-3">
                                <div
                                  className="p-3 rounded-lg dark:border-gray-600 border-gray-400 border"
                                  style={{
                                    background:
                                      'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 50% / 20px 20px',
                                  }}
                                >
                                  <img
                                    src={logoCompactPreview}
                                    alt="Compact Logo Preview"
                                    className="w-16 h-16 object-contain"
                                  />
                                </div>
                                <div className="flex-1">
                                  {logoCompactDimensions && (
                                    <p className="text-xs dark:text-[#D8D8D8] text-textSecondary mb-2">
                                      <span className="dark:text-purple-300 text-purple-600">
                                        Dimensions:
                                      </span>{' '}
                                      {logoCompactDimensions.width}×
                                      {logoCompactDimensions.height}px
                                    </p>
                                  )}
                                  <button
                                    onClick={() => handleRemoveLogo('compact')}
                                    className="px-3 py-1 text-xs dark:text-red-400 text-red-600 dark:hover:text-red-300 hover:text-red-500 border border-red-400/30 rounded hover:bg-red-500/10 transition-colors flex items-center gap-1"
                                  >
                                    <X className="w-3 h-3" />
                                    Remove
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="text-xs dark:text-gray-400 text-gray-500 space-y-1">
                            <p>
                              • <strong>Recommended:</strong> 128×128px to
                              256×256px
                            </p>
                            <p>
                              • <strong>Minimum:</strong> 64×64px |{' '}
                              <strong>Maximum:</strong> 512×512px
                            </p>
                            <p>
                              • <strong>Aspect ratio:</strong> Must be square
                              (1:1 ratio)
                            </p>
                            <p>
                              • <strong>Format:</strong> PNG (transparent
                              background recommended)
                            </p>
                            <p>
                              • <strong>Max file size:</strong> 2MB
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ══ EMAIL AGENT ══ */}
              {selectedBox === 'email_agent' && (
                <>
                  <div className="dark:glass-effect bg-bgPrimary dark:border-0 border-2 border-gray-200 p-6 rounded-lg space-y-4">
                    <div className="space-y-1">
                      <label className="text-[11px] tracking-wider dark:text-white/60 text-textSecondary font-medium uppercase flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5" /> Email Job Application
                        Monitor
                      </label>
                    </div>
                    <p className="dark:text-[#D8D8D8] text-textSecondary text-sm">
                      Automatically capture candidates who email their resumes
                      directly to your inbox.
                    </p>

                    {emailMonitorLoading ? (
                      <div className="animate-pulse dark:bg-gray-700/50 bg-gray-200 rounded h-10 w-48" />
                    ) : emailMonitorSession ? (
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                          <span className="dark:text-[#D9D9D9] text-textPrimary text-sm font-medium">
                            {emailMonitorSession.email}
                          </span>
                          <span className="px-2 py-0.5 bg-blue-500/20 dark:text-blue-300 text-blue-600 text-[10px] rounded-full border border-blue-400/30 font-bold uppercase tracking-wider">
                            {emailMonitorSession.provider}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={emailMonitorSession.status === 'active'}
                              onChange={handleToggleEmailMonitor}
                              disabled={emailMonitorToggling}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                          </label>
                          <span className="dark:text-[#D8D8D8] text-textSecondary text-sm">
                            {emailMonitorSession.status === 'active'
                              ? 'Monitoring active'
                              : 'Monitoring paused'}
                          </span>
                          <button
                            onClick={handleDisconnectEmailMonitor}
                            disabled={emailMonitorDisconnecting}
                            className="ml-auto flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-red-500 border border-red-400/40 rounded-full hover:bg-red-500/10 transition-all disabled:opacity-50"
                          >
                            {emailMonitorDisconnecting ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Unlink className="w-3.5 h-3.5" />
                            )}
                            Disconnect
                          </button>
                        </div>
                      </div>
                    ) : pwForm.show ? (
                      <form
                        onSubmit={handleConnectPassword}
                        autoComplete="off"
                        className="flex flex-col gap-4 max-w-md"
                      >
                        <div className="relative">
                          <select
                            value={pwForm.provider}
                            onChange={(e) =>
                              setPwForm((f) => ({
                                ...f,
                                provider: e.target.value,
                              }))
                            }
                            className="w-full appearance-none px-4 py-3 pr-10 text-sm rounded-lg border dark:border-white/10 border-gray-300 dark:bg-white/5 bg-white dark:text-[#D9D9D9] text-textPrimary cursor-pointer focus:ring-1 focus:ring-cyan-500 outline-none transition"
                          >
                            <option value="gmail" className="dark:bg-gray-900">
                              Gmail
                            </option>
                            <option
                              value="outlook"
                              className="dark:bg-gray-900"
                            >
                              Outlook
                            </option>
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 dark:text-gray-400 text-gray-500" />
                        </div>
                        <input
                          type="email"
                          placeholder="your@email.com"
                          autoComplete="off"
                          value={pwForm.email}
                          onChange={(e) => {
                            const email = e.target.value;
                            const domain =
                              email.split('@')[1]?.toLowerCase() || '';
                            const isGmail =
                              domain === 'gmail.com' ||
                              domain === 'googlemail.com';
                            setPwForm((f) => ({
                              ...f,
                              email,
                              ...(email.includes('@')
                                ? { provider: isGmail ? 'gmail' : 'outlook' }
                                : {}),
                            }));
                          }}
                          className="px-4 py-3 text-sm rounded-lg border dark:border-white/10 border-gray-300 dark:bg-white/5 bg-white dark:text-[#D9D9D9] text-textPrimary focus:ring-1 focus:ring-cyan-500 outline-none transition"
                          required
                        />
                        <div className="relative">
                          <input
                            type={pwForm.showPass ? 'text' : 'password'}
                            placeholder="App password (16 characters)"
                            autoComplete="new-password"
                            value={pwForm.password}
                            onChange={(e) =>
                              setPwForm((f) => ({
                                ...f,
                                password: e.target.value,
                              }))
                            }
                            className="w-full px-4 py-3 pr-10 text-sm rounded-lg border dark:border-white/10 border-gray-300 dark:bg-white/5 bg-white dark:text-[#D9D9D9] text-textPrimary focus:ring-1 focus:ring-cyan-500 outline-none transition"
                            required
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setPwForm((f) => ({
                                ...f,
                                showPass: !f.showPass,
                              }))
                            }
                            className="absolute right-3 top-1/2 -translate-y-1/2 dark:text-gray-400 text-gray-500 hover:text-pink-500 transition-colors"
                          >
                            {pwForm.showPass ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() =>
                              setShowAppPasswordGuide((prev) => !prev)
                            }
                            className="flex items-center gap-1 text-xs dark:text-blue-400 text-blue-500 hover:underline"
                          >
                            <ChevronDown
                              className={`w-3 h-3 transition-transform ${showAppPasswordGuide ? 'rotate-180' : ''}`}
                            />
                            How to get an App Password?
                          </button>
                          {showAppPasswordGuide && (
                            <ol className="mt-2 pl-1 space-y-1 text-xs dark:text-gray-400 text-gray-500 list-decimal list-inside">
                              {pwForm.provider === 'gmail' ? (
                                <>
                                  <li>
                                    Go to{' '}
                                    <a
                                      href="https://myaccount.google.com/apppasswords"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="dark:text-blue-400 text-blue-500 underline"
                                    >
                                      myaccount.google.com/apppasswords
                                    </a>
                                  </li>
                                  <li>
                                    Sign in and enable{' '}
                                    <strong className="dark:text-gray-300 text-gray-600">
                                      2-Step Verification
                                    </strong>{' '}
                                    first if not done
                                  </li>
                                  <li>
                                    Enter an app name (e.g. "GenieHire") and
                                    click{' '}
                                    <strong className="dark:text-gray-300 text-gray-600">
                                      Create
                                    </strong>
                                  </li>
                                  <li>
                                    Copy the 16-character password and paste it
                                    above
                                  </li>
                                </>
                              ) : (
                                <>
                                  <li>
                                    Go to{' '}
                                    <a
                                      href="https://mysignins.microsoft.com/security-info"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="dark:text-blue-400 text-blue-500 underline"
                                    >
                                      mysignins.microsoft.com/security-info
                                    </a>{' '}
                                    <span className="dark:text-gray-500 text-gray-400">
                                      (Add method → App password)
                                    </span>
                                  </li>
                                  <li>
                                    Click{' '}
                                    <strong className="dark:text-gray-300 text-gray-600">
                                      Create
                                    </strong>
                                    , enter a name (e.g. "GenieHire")
                                  </li>
                                  <li>
                                    Copy the generated password and paste it
                                    above
                                  </li>
                                  <li className="dark:text-yellow-500 text-yellow-600">
                                    Note: requires MFA enabled on your Microsoft
                                    account
                                  </li>
                                </>
                              )}
                            </ol>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="submit"
                            disabled={pwForm.connecting}
                            className="button !py-2 !px-6"
                          >
                            <span className="flex items-center gap-2">
                              {pwForm.connecting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <KeyRound className="w-4 h-4" />
                              )}
                              Connect
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setPwForm((f) => ({
                                ...f,
                                show: false,
                                email: '',
                                password: '',
                              }))
                            }
                            className="px-6 py-2 text-sm rounded-full border-2 dark:border-white/10 border-gray-200 dark:text-gray-400 text-textSecondary dark:hover:bg-white/5 hover:bg-gray-100 transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <button
                        onClick={() => setPwForm((f) => ({ ...f, show: true }))}
                        className="button !py-2.5 !px-8"
                      >
                        <span className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Connect with App Password
                        </span>
                      </button>
                    )}
                  </div>
                </>
              )}
              <div className="flex justify-end w-full pt-4">
                {selectedBox === 'company_profile' ? (
                  <button
                    type="submit"
                    form="company-profile-form"
                    data-guide="save-company-profile"
                    className="px-6 py-2.5 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-sm transition-colors"
                  >
                    <span className="flex gap-2 items-center">
                      <Save className="w-4 h-4" />
                      Save Configuration
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={handleSaveGeneralConfig}
                    disabled={isSaving || !hasChanges}
                    className="px-6 py-2.5 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-sm transition-colors disabled:bg-none dark:disabled:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-100 disabled:text-white/50"
                  >
                    <span className="flex gap-2 items-center">
                      {isSaving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {isSaving ? 'Saving...' : 'Save Configuration'}
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentConfig;
